import { mdiArrowULeftTop, mdiChevronDown, mdiChevronUp } from '@mdi/js';
import Icon from '@mdi/react';
import { formatDate } from '../../services/primitives/date-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { CellProps } from 'react-table';
import { ClaimWorkflowHistoryItem } from '../../api';
import { ClaimProgressStore } from '../../stores';
import { Spinner } from '../base';
import { InfiniteScrollDataTablePresenter, ITableColumn } from '../data-table';
import { ClaimLiveStatusCard, isPausedState } from './claim-live-status-card';
import { ClaimStageTracker, getMacroStageIndex, getMacroStageLabel } from './claim-stage-tracker';
import * as S from './claim-progress-tracker.styles';

interface IClaimProgressTrackerProps {
    claimId: string;
    claimProgressStore: ClaimProgressStore;
}

interface IActivityFeedRow {
    id: string;
    date: string;
    stage: string;
    action: string;
    actionLabel?: string;
    actionDescription?: string;
    isLoopTransition: boolean;
}

const EMPTY_VALUE = '-';
const TITLE = 'Claim Progress';

function toTimestamp(value?: string): number {
    if (!value) return 0;
    const ts = new Date(value).getTime();
    return Number.isFinite(ts) ? ts : 0;
}

const hasHaltedToken = (...tokens: Array<string | undefined>): boolean => {
    return tokens.some(token => (token || '').toUpperCase().includes('HALTED'));
};

const isHaltedItem = (item: { workflowStatus?: string; workflowStep?: string; action?: string }): boolean => {
    return hasHaltedToken(item.workflowStatus, item.workflowStep, item.action);
};

const isStageFrozenItem = (item: { workflowStatus?: string; workflowStep?: string; action?: string }): boolean => {
    return (
        isHaltedItem(item) ||
        isPausedState(item.workflowStatus, item.workflowStep, item.action)
    );
};

const getPreviousActiveStageToken = (items: ClaimWorkflowHistoryItem[], index: number): string | undefined => {
    for (let i = index + 1; i < items.length; i += 1) {
        const candidate = items[i];
        if (!isStageFrozenItem(candidate)) {
            return candidate.workflowStatus || candidate.workflowStep;
        }
    }
    return undefined;
};

const getEffectiveStageToken = (
    item: ClaimWorkflowHistoryItem,
    items: ClaimWorkflowHistoryItem[],
    index: number,
): string | undefined => {
    if (isStageFrozenItem(item)) {
        return getPreviousActiveStageToken(items, index) || item.workflowStatus || item.workflowStep;
    }

    return item.workflowStatus || item.workflowStep;
};

/** `items` sorted newest-first; loop when this event's macro stage moves backward vs the next older event. */
const isMacroStageRegressionAtIndex = (items: ClaimWorkflowHistoryItem[], index: number): boolean => {
    if (index < 0 || index >= items.length - 1) {
        return false;
    }
    const newerIdx = getMacroStageIndex(getEffectiveStageToken(items[index], items, index));
    const olderIdx = getMacroStageIndex(getEffectiveStageToken(items[index + 1], items, index + 1));
    return newerIdx < olderIdx;
};

const mapHistoryToRows = (items: ClaimWorkflowHistoryItem[]): IActivityFeedRow[] => {
    return items.map((item, index) => ({
        id: `${item.actionDate || 'unknown'}-${item.workflowStep || 'stage'}-${index}`,
        date: item.actionDate || '',
        stage: getMacroStageLabel(getEffectiveStageToken(item, items, index)),
        action: item.action || EMPTY_VALUE,
        actionLabel: item.actionLabel,
        actionDescription: item.actionDescription,
        isLoopTransition: isMacroStageRegressionAtIndex(items, index),
    }));
};

interface IClaimProgressTrackerCardProps {
    expanded: boolean;
    onToggleExpanded: () => void;
    children?: React.ReactNode;
}

const ClaimProgressTrackerCard: React.FC<IClaimProgressTrackerCardProps> = ({
    expanded,
    onToggleExpanded,
    children,
}) => (
    <S.StyledTitleCard
        size="large"
        title={TITLE}
        caption={
            <S.HeaderToggleButton
                type="button"
                variant="text"
                onClick={onToggleExpanded}
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide claim progress details' : 'Show claim progress details'}>
                <S.HeaderToggleIcon path={expanded ? mdiChevronUp : mdiChevronDown} size={1} />
            </S.HeaderToggleButton>
        }>
        {expanded && children}
    </S.StyledTitleCard>
);

const ClaimProgressTrackerComponent: React.FC<IClaimProgressTrackerProps> = ({ claimId, claimProgressStore }) => {
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        claimProgressStore.fetch(claimId);

        return () => {
            claimProgressStore.reset();
        };
    }, [claimId, claimProgressStore]);

    const toggleExpanded = () => setExpanded(prev => !prev);

    if (claimProgressStore.loading) {
        return (
            <ClaimProgressTrackerCard expanded={expanded} onToggleExpanded={toggleExpanded}>
                <S.CardBody>
                    <S.LoadingContainer>
                        <Spinner />
                    </S.LoadingContainer>
                </S.CardBody>
            </ClaimProgressTrackerCard>
        );
    }

    if (claimProgressStore.error) {
        return (
            <ClaimProgressTrackerCard expanded={expanded} onToggleExpanded={toggleExpanded}>
                <S.CardBody>
                    <S.ErrorContainer>
                        <S.ErrorMessage>{claimProgressStore.error}</S.ErrorMessage>
                        <S.RetryButton variant="primary" onClick={() => claimProgressStore.fetch(claimId)}>
                            Retry
                        </S.RetryButton>
                    </S.ErrorContainer>
                </S.CardBody>
            </ClaimProgressTrackerCard>
        );
    }

    return (
        <ClaimProgressTrackerCard expanded={expanded} onToggleExpanded={toggleExpanded}>
            <ClaimProgressTrackerContent claimProgressStore={claimProgressStore} />
        </ClaimProgressTrackerCard>
    );
};

const ClaimProgressTrackerContent: React.FC<Pick<IClaimProgressTrackerProps, 'claimProgressStore'>> = observer(
    ({ claimProgressStore }) => {
        const sortedHistory = [...claimProgressStore.data].sort(
            (a, b) => toTimestamp(b.actionDate) - toTimestamp(a.actionDate),
        );
        const rows = mapHistoryToRows(sortedHistory);
        const latestItem = sortedHistory[0];
        const latestIsHalted = latestItem ? isHaltedItem(latestItem) : false;
        const latestIsWorkersPaused = latestItem
            ? isPausedState(latestItem.workflowStatus, latestItem.workflowStep, latestItem.action)
            : false;
        const latestEffectiveStageToken = latestItem ? getEffectiveStageToken(latestItem, sortedHistory, 0) : undefined;
        const currentStageIndex = getMacroStageIndex(latestEffectiveStageToken);
        const latestHasLoop = isMacroStageRegressionAtIndex(sortedHistory, 0);

        const columns: ITableColumn<IActivityFeedRow>[] = useMemo(
            () => [
                {
                    id: 'Date',
                    Header: 'Date',
                    accessor: 'date',
                    Cell: ({ value }): React.ReactElement => (
                        <>{value ? formatDate(value, 'MMM d, yyyy h:mma') : EMPTY_VALUE}</>
                    ),
                    minWidth: 150,
                    maxWidth: 200,
                },
                {
                    id: 'Stage',
                    Header: 'Stage',
                    accessor: 'stage',
                    Cell: ({ value }): React.ReactElement => <S.StageTag>{value || EMPTY_VALUE}</S.StageTag>,
                    minWidth: 120,
                    maxWidth: 200,
                    width: '130px',
                },
                {
                    id: 'Action',
                    Header: 'Action',
                    accessor: 'actionLabel',
                    Cell: ({ value }): React.ReactElement => <>{value || EMPTY_VALUE}</>,
                    minWidth: 120,
                },
                {
                    id: 'Description',
                    Header: 'Description',
                    accessor: 'actionDescription',
                    Cell: ({ row }: CellProps<IActivityFeedRow>): React.ReactElement => {
                        const text = row.original.actionDescription || EMPTY_VALUE;
                        if (text === EMPTY_VALUE) {
                            return <S.DescriptionText>{EMPTY_VALUE}</S.DescriptionText>;
                        }
                        if (row.original.isLoopTransition) {
                            return (
                                <S.DescriptionLoopRow>
                                    <Icon path={mdiArrowULeftTop} size={0.75} />
                                    <span>{text}</span>
                                </S.DescriptionLoopRow>
                            );
                        }
                        return <S.DescriptionText>{text}</S.DescriptionText>;
                    },
                    minWidth: 400,
                },
            ],
            [],
        );

        const presenter = React.useMemo(
            () =>
                new InfiniteScrollDataTablePresenter<IActivityFeedRow>({
                    name: 'claim-progress-activity-feed',
                    data: [],
                    columns,
                    filterSchemas: [],
                }),
            [columns],
        );

        useEffect(() => {
            presenter.resetData();
            presenter.onDataUpdate(rows);
        }, [presenter, rows]);

        return (
            <S.CardBody>
                <ClaimStageTracker
                    currentStageIndex={currentStageIndex}
                    isPaused={latestIsHalted || latestIsWorkersPaused}
                    pauseReason={latestIsWorkersPaused ? 'workersPaused' : latestIsHalted ? 'halted' : undefined}
                />
                <ClaimLiveStatusCard
                    step={latestItem?.workflowStep}
                    stepLabel={latestItem?.workflowStepLabel}
                    action={latestItem?.action}
                    actionLabel={latestItem?.actionLabel}
                    actionDescription={latestItem?.actionDescription}
                    workflowStatus={latestItem?.workflowStatus}
                    workflowStatusLabel={latestItem?.workflowStatusLabel}
                    hasLoop={latestHasLoop}
                />
                <S.TableCard>
                    <S.ActivityFeedTable
                        presenter={presenter}
                        hideFiltering={true}
                        hideNavigation={true}
                        hideSettings={true}
                        showExport={false}
                        enableInfiniteScroll={false}
                        disableSorting={true}
                    />
                </S.TableCard>
            </S.CardBody>
        );
    },
);

export const ClaimProgressTracker = observer(ClaimProgressTrackerComponent);
